import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiUser, FiPhone, FiMail, FiMapPin, FiHome, FiChevronRight, FiRefreshCw, FiCreditCard, FiDollarSign, FiPlus, FiTrash2, FiEdit2, FiLink, FiCopy, FiCheckCircle, FiToggleLeft, FiToggleRight, FiSettings } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { clientesAPI, ibgeAPI, parametrosCobrancaAPI, cartoesCreditoAPI, cartaoPublicoAPI, contratosAPI, configuracoesAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};

  &:hover {
    background: ${theme.colors.background.primary};
    border-color: ${theme.colors.text.muted};
    color: ${theme.colors.text.primary};
  }
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.background.primary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[500]};
  }

  &.delete:hover {
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[500]};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
`;

const InfoMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.primary[500]}15;
  border: 1px solid ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.primary[600]};
  font-size: ${theme.fontSizes.sm};
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? theme.colors.primary[500] : theme.colors.text.secondary};
`;

const StepIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $active }) =>
    $active ? theme.colors.primary[100] : theme.colors.background.primary};
  border: 2px solid
    ${({ $active }) => ($active ? theme.colors.primary[500] : theme.colors.border.primary)};
  color: ${({ $active }) => ($active ? theme.colors.primary[600] : theme.colors.text.secondary)};
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
`;

const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const StepsButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.primary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UpdateButton = styled(PrimaryButton)`
  background: ${theme.colors.warning[500]};
  color: ${theme.colors.text.primary};

  &:hover {
    background: ${theme.colors.warning[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.warning[500]}40;
  }
`;

const CartaoTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  margin-left: ${theme.spacing[2]};
  background: ${props => {
    if (props.$type === 'ativo') return `${theme.colors.success[500]}20`;
    if (props.$type === 'inativo') return `${theme.colors.error[500]}20`;
    if (props.$type === 'principal') return `${theme.colors.primary[500]}20`;
    return `${theme.colors.text.muted}20`;
  }};
  color: ${props => {
    if (props.$type === 'ativo') return theme.colors.success[600];
    if (props.$type === 'inativo') return theme.colors.error[600];
    if (props.$type === 'principal') return theme.colors.primary[600];
    return theme.colors.text.secondary;
  }};
  border: 1px solid ${props => {
    if (props.$type === 'ativo') return theme.colors.success[500];
    if (props.$type === 'inativo') return theme.colors.error[500];
    if (props.$type === 'principal') return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
`;

const asaasConsultaInitialState = {
  loading: false,
  found: false,
  data: null,
  error: null
};

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClienteForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [clienteId, setClienteId] = useState(isEditMode ? id : null);
  const [clienteUuid, setClienteUuid] = useState(
    isEditMode && typeof id === 'string' && id.includes('-') ? id : null
  );
  const [clienteAsaasId, setClienteAsaasId] = useState(null);
  const [asaasConsulta, setAsaasConsulta] = useState(asaasConsultaInitialState);
  
  const [formData, setFormData] = useState({
    nome_fantasia: '',
    nome_social: '',
    nome_contato: '',
    fone_contato: '',
    emial_contato: '',
    cidade: '',
    estado: '',
    ativo: true,
    cpf_cnpj: '',
    tipo_pessoa: '',
    observacoes: '',
    endereco: '',
    numero: '',
    complemento: '',
    cep: ''
  });

  // Dados do cadastro Asaas
  const [formAsaas, setFormAsaas] = useState({
    nome: '',
    cpfCnpj: '',
    email: '',
    mobilePhone: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    postalCode: '',
    externalReference: '',
    notificationDisabled: false,
    foreignCustomer: false,
    groupName: 'HaevoRevendas',
    additionalEmails: ''
  });

  // Dados dos parâmetros de cobrança
  const [formParametrosCobranca, setFormParametrosCobranca] = useState({
    forma_pagamento_mensalidade: 'C',
    dias_tolerancia_vencimento: 5,
    enviar_notificacao_antes_vencimento: true,
    dias_antes_vencimento_notificacao: 3,
    enviar_notificacao_apos_vencimento: true,
    aplicar_desconto_antecipacao: false,
    percentual_desconto_antecipacao: 0,
    aplicar_multa_atraso: true,
    percentual_multa: 2.0,
    aplicar_juros_atraso: true,
    percentual_juros_mensal: 1.0
  });

  // Valores originais dos parâmetros de cobrança (para detectar mudanças)
  const [parametrosCobrancaOriginal, setParametrosCobrancaOriginal] = useState(null);
  const [temParametrosCobrancaNoBanco, setTemParametrosCobrancaNoBanco] = useState(false);

  // Estado para link de cadastro público de cartão
  const [linkCartaoGerado, setLinkCartaoGerado] = useState(null);
  const [linkCopiado, setLinkCopiado] = useState(false);

  // Dados dos cartões de crédito
  const [cartoes, setCartoes] = useState([]);
  const [formCartao, setFormCartao] = useState({
    holder_name: '',
    number_last_four: '',
    expiry_month: '',
    expiry_year: '',
    holder_cpf_cnpj: '',
    holder_email: '',
    holder_phone: '',
    holder_mobile_phone: '',
    holder_postal_code: '',
    holder_address: '',
    holder_address_number: '',
    holder_address_complement: '',
    padrao: false,
    observacoes: ''
  });
  const [editandoCartao, setEditandoCartao] = useState(null);
  const [showDeleteCartaoModal, setShowDeleteCartaoModal] = useState(false);
  const [cartaoParaDeletar, setCartaoParaDeletar] = useState(null);
  const [deletingCartao, setDeletingCartao] = useState(false);

  // Dados dos parâmetros do sistema (configurações)
  const [formParametrosSistema, setFormParametrosSistema] = useState({
    supabase_imagens_base_url: '',
    evolution_api_url: '',
    evolution_api_key: '',
    evolution_instance: ''
  });
  const [parametrosSistemaOriginal, setParametrosSistemaOriginal] = useState(null);
  const [temParametrosSistemaNoBanco, setTemParametrosSistemaNoBanco] = useState(false);
  const [configuracaoUuid, setConfiguracaoUuid] = useState(null);

  const preencherFormularioComDadosCliente = useCallback(() => {
    const cpfCnpjNumerico = (formData.cpf_cnpj || '').replace(/\D/g, '');
    const telefoneNumerico = (formData.fone_contato || '').replace(/\D/g, '');
    const cepNumerico = (formData.cep || '').replace(/\D/g, '');
    const externalReferenceValue = clienteUuid || (clienteId ? String(clienteId) : '');

    setFormAsaas((prev) => ({
      nome: prev.nome || formData.nome_fantasia || formData.nome_social || '',
      cpfCnpj: prev.cpfCnpj || cpfCnpjNumerico,
      email: prev.email || formData.emial_contato || '',
      mobilePhone: prev.mobilePhone || telefoneNumerico,
      address: prev.address || formData.endereco || '',
      addressNumber: prev.addressNumber || formData.numero || '',
      complement: formData.complemento || prev.complement || '',
      province: prev.province || formData.cidade || '',
      postalCode: prev.postalCode || cepNumerico,
      externalReference: externalReferenceValue || prev.externalReference || '',
      notificationDisabled: prev.notificationDisabled,
      foreignCustomer: prev.foreignCustomer,
      groupName: prev.groupName || 'HaevoRevendas',
      additionalEmails: prev.additionalEmails || ''
    }));
  }, [formData, clienteUuid, clienteId]);

  const preencherFormularioComDadosAsaas = useCallback((clienteAsaas) => {
    if (!clienteAsaas) return;

    setClienteAsaasId(clienteAsaas.id || null);
    setFormAsaas({
      nome: clienteAsaas.name || '',
      cpfCnpj: clienteAsaas.cpfCnpj || '',
      email: clienteAsaas.email || '',
      mobilePhone: clienteAsaas.mobilePhone || clienteAsaas.phone || '',
      address: clienteAsaas.address || '',
      addressNumber: clienteAsaas.addressNumber || '',
      complement: clienteAsaas.complement || '',
      province: clienteAsaas.province || '',
      postalCode: clienteAsaas.postalCode || '',
      externalReference: clienteAsaas.externalReference || clienteUuid || '',
      notificationDisabled: clienteAsaas.notificationDisabled ?? false,
      foreignCustomer: clienteAsaas.foreignCustomer ?? false,
      groupName: clienteAsaas.groupName || 'HaevoRevendas',
      additionalEmails: clienteAsaas.additionalEmails || ''
    });
  }, [clienteUuid]);

  const consultarAsaas = useCallback(async () => {
    if (!clienteUuid) {
      setAsaasConsulta(asaasConsultaInitialState);
      return;
    }

    setAsaasConsulta({
      loading: true,
      found: false,
      data: null,
      error: null
    });

    try {
      const response = await clientesAPI.consultarAsaas(clienteUuid);
      const lista = Array.isArray(response.data?.list)
        ? response.data.list
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      const clienteAsaas = lista.length > 0 ? lista[0] : null;

      if (clienteAsaas) {
        setAsaasConsulta({
          loading: false,
          found: true,
          data: clienteAsaas,
          error: null
        });
        preencherFormularioComDadosAsaas(clienteAsaas);
      } else {
        setClienteAsaasId(null);
        setAsaasConsulta({
          loading: false,
          found: false,
          data: null,
          error: null
        });
      }
    } catch (err) {
      setAsaasConsulta({
        loading: false,
        found: false,
        data: null,
        error: err.response?.data?.message || 'Não foi possível consultar a API Asaas'
      });
    }
  }, [clienteUuid, preencherFormularioComDadosAsaas]);

  // Carregar dados do cliente se estiver editando
  useEffect(() => {
    const carregarCliente = async () => {
      if (!id) return;
      
      try {
        setLoadingData(true);
        const response = await clientesAPI.buscarPorId(id);
        
        if (response.data.success) {
          const cliente = response.data.data;
          
          // Mapear valores antigos de tipo_pessoa para novos
          const mappedCliente = {
            ...cliente,
            tipo_pessoa: cliente.tipo_pessoa === 'Física' ? 'fisica' : 
                         cliente.tipo_pessoa === 'Jurídica' ? 'pj' : 
                         cliente.tipo_pessoa
          };
          
          // Garantir que todos os campos de string sejam sempre strings (nunca null ou undefined)
          setFormData({
            nome_fantasia: mappedCliente.nome_fantasia || '',
            nome_social: mappedCliente.nome_social || '',
            nome_contato: mappedCliente.nome_contato || '',
            fone_contato: mappedCliente.fone_contato || '',
            emial_contato: mappedCliente.emial_contato || '',
            cidade: mappedCliente.cidade || '',
            estado: mappedCliente.estado || '',
            ativo: mappedCliente.ativo !== undefined ? mappedCliente.ativo : true,
            cpf_cnpj: mappedCliente.cpf_cnpj || '',
            tipo_pessoa: mappedCliente.tipo_pessoa || '',
            observacoes: mappedCliente.observacoes || '',
            endereco: mappedCliente.endereco || '',
            numero: mappedCliente.numero || '',
            complemento: mappedCliente.complemento || '',
            cep: mappedCliente.cep || ''
          });
          
          setClienteId(mappedCliente.id ? String(mappedCliente.id) : null);
          setClienteUuid(mappedCliente.uuid || null);
          setClienteAsaasId(mappedCliente.id_customer_asaas || null);
          
          // Carregar dados no formulário Asaas
          const cpfCnpjNumerico = (mappedCliente.cpf_cnpj || '').replace(/\D/g, '');
          setFormAsaas({
            nome: mappedCliente.nome_fantasia || mappedCliente.nome_social || '',
            cpfCnpj: cpfCnpjNumerico,
            email: mappedCliente.emial_contato || '',
            mobilePhone: (mappedCliente.fone_contato || '').replace(/\D/g, ''),
            address: mappedCliente.endereco || '',
            addressNumber: mappedCliente.numero || '',
            complement: mappedCliente.complemento || '',
            province: mappedCliente.cidade || '',
            postalCode: (mappedCliente.cep || '').replace(/\D/g, ''),
            externalReference: mappedCliente.uuid || (mappedCliente.id ? String(mappedCliente.id) : ''),
            notificationDisabled: false,
            foreignCustomer: false,
            groupName: 'HaevoRevendas',
            additionalEmails: ''
          });
        } else {
          setError('Erro ao carregar dados do cliente');
        }
      } catch (err) {
        setError('Erro ao conectar com o servidor');
      } finally {
        setLoadingData(false);
      }
    };

    if (isEditMode) {
      carregarCliente();
    }
  }, [id, isEditMode]);

  // Buscar lista de estados (IBGE) no modo criação
  useEffect(() => {
    const carregarEstados = async () => {
      try {
        const resp = await ibgeAPI.listarEstados();
        const ordenados = (resp.data || []).sort((a, b) => a.nome.localeCompare(b.nome));
        setEstados(ordenados);
      } catch (e) {
        // Erro ao carregar estados
      }
    };

    if (!isEditMode) {
      carregarEstados();
    }
  }, [isEditMode]);

  // Buscar cidades ao alterar o estado (IBGE) no modo criação
  useEffect(() => {
    const carregarCidades = async (uf) => {
      try {
        const resp = await ibgeAPI.listarMunicipios(uf);
        const ordenadas = (resp.data || []).sort((a, b) => a.nome.localeCompare(b.nome));
        setCidades(ordenadas);
      } catch (e) {
        // Erro ao carregar municípios
      }
    };

    if (!isEditMode && formData.estado) {
      carregarCidades(formData.estado);
    } else if (!isEditMode) {
      setCidades([]);
    }
  }, [isEditMode, formData.estado]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      if (field === 'tipo_pessoa') {
        const numerosDocumento = (prev.cpf_cnpj || '').replace(/\D/g, '');
        let cpfCnpjAtualizado = prev.cpf_cnpj || '';

        if (value === 'fisica') {
          cpfCnpjAtualizado = numerosDocumento ? aplicarMascaraCPF(numerosDocumento) : '';
        } else if (value === 'pj') {
          cpfCnpjAtualizado = numerosDocumento ? aplicarMascaraCNPJ(numerosDocumento) : '';
        }

        return {
          ...prev,
          tipo_pessoa: value,
          cpf_cnpj: cpfCnpjAtualizado
        };
      }

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleEstadoChange = (uf) => {
    setFormData((prev) => ({
      ...prev,
      estado: uf,
      cidade: ''
    }));
  };

  // Funções de máscara
  const aplicarMascaraTelefone = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros.length <= 10) {
      return apenasNumeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return apenasNumeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const aplicarMascaraCEP = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    return apenasNumeros.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const aplicarMascaraCPF = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '').slice(0, 11);

    if (apenasNumeros.length <= 3) {
      return apenasNumeros;
    }

    if (apenasNumeros.length <= 6) {
      return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3)}`;
    }

    if (apenasNumeros.length <= 9) {
      return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6)}`;
    }

    return `${apenasNumeros.slice(0, 3)}.${apenasNumeros.slice(3, 6)}.${apenasNumeros.slice(6, 9)}-${apenasNumeros.slice(9)}`;
  };

  const aplicarMascaraCNPJ = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '').slice(0, 14);

    if (apenasNumeros.length <= 2) {
      return apenasNumeros;
    }

    if (apenasNumeros.length <= 5) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2)}`;
    }

    if (apenasNumeros.length <= 8) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5)}`;
    }

    if (apenasNumeros.length <= 12) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5, 8)}/${apenasNumeros.slice(8)}`;
    }

    return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5, 8)}/${apenasNumeros.slice(8, 12)}-${apenasNumeros.slice(12)}`;
  };

  const validarCPF = (valor) => {
    const cpf = valor.replace(/\D/g, '');
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
      return false;
    }

    let soma = 0;
    for (let i = 0; i < 9; i += 1) {
      soma += parseInt(cpf[i], 10) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    if (resto !== parseInt(cpf[9], 10)) {
      return false;
    }

    soma = 0;
    for (let i = 0; i < 10; i += 1) {
      soma += parseInt(cpf[i], 10) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    return resto === parseInt(cpf[10], 10);
  };

  const validarCNPJ = (valor) => {
    const cnpj = valor.replace(/\D/g, '');
    if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
      return false;
    }

    const calcularDigito = (base) => {
      let peso = base.length - 7;
      let soma = 0;

      for (let i = 0; i < base.length; i += 1) {
        soma += parseInt(base[i], 10) * peso;
        peso -= 1;
        if (peso < 2) {
          peso = 9;
        }
      }

      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const base = cnpj.slice(0, 12);
    const digito1 = calcularDigito(base);
    const digito2 = calcularDigito(base + digito1);

    return cnpj === `${base}${digito1}${digito2}`;
  };

  const handleInputMaskedChange = (field, value, tipoMascara) => {
    let valorAplicado = value;
    
    if (tipoMascara === 'telefone') {
      valorAplicado = aplicarMascaraTelefone(value);
    } else if (tipoMascara === 'cep') {
      valorAplicado = aplicarMascaraCEP(value);
    } else if (tipoMascara === 'cpf') {
      valorAplicado = aplicarMascaraCPF(value);
    } else if (tipoMascara === 'cnpj') {
      valorAplicado = aplicarMascaraCNPJ(value);
    } else if (tipoMascara === 'cpf_cnpj') {
      const tipoPessoaAtual = formData.tipo_pessoa;

      if (tipoPessoaAtual === 'fisica') {
        valorAplicado = aplicarMascaraCPF(value);
      } else if (tipoPessoaAtual === 'pj') {
        valorAplicado = aplicarMascaraCNPJ(value);
      } else {
        const apenasNumeros = value.replace(/\D/g, '');
        valorAplicado = apenasNumeros.length <= 11
          ? aplicarMascaraCPF(value)
          : aplicarMascaraCNPJ(value);
      }
    }
    
    handleInputChange(field, valorAplicado);
  };

  const montarPayloadAsaas = useCallback(() => {
    const sanitizeDigits = (valor) => {
      if (valor === null || valor === undefined) return null;
      const apenasNumeros = String(valor).replace(/\D/g, '');
      return apenasNumeros.length > 0 ? apenasNumeros : null;
    };

    const payload = {
      name: formAsaas.nome || null,
      cpfCnpj: sanitizeDigits(formAsaas.cpfCnpj),
      email: formAsaas.email || null,
      phone: sanitizeDigits(formAsaas.phone),
      mobilePhone: sanitizeDigits(formAsaas.mobilePhone),
      address: formAsaas.address || null,
      addressNumber: formAsaas.addressNumber || null,
      complement: formAsaas.complement || null,
      province: formAsaas.province || null,
      postalCode: sanitizeDigits(formAsaas.postalCode),
      externalReference:
        formAsaas.externalReference ||
        clienteUuid ||
        (clienteId ? String(clienteId) : null),
      notificationDisabled: formAsaas.notificationDisabled || false,
      additionalEmails: formAsaas.additionalEmails || null,
      groupName: formAsaas.groupName || 'HaevoRevendas',
      foreignCustomer: formAsaas.foreignCustomer || false
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    return payload;
  }, [formAsaas, clienteUuid, clienteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);

    const tipoPessoa = formData.tipo_pessoa;
    const documentoNumerico = (formData.cpf_cnpj || '').replace(/\D/g, '');

    if (!tipoPessoa) {
      setError('Selecione o tipo de pessoa (Física ou Jurídica).');
      return;
    }

    if (!documentoNumerico) {
      setError(tipoPessoa === 'fisica' ? 'Informe um CPF válido.' : 'Informe um CNPJ válido.');
      return;
    }

    if (tipoPessoa === 'fisica' && documentoNumerico.length !== 11) {
      setError('Informe um CPF válido com 11 dígitos.');
      return;
    }

    if (tipoPessoa === 'fisica' && !validarCPF(documentoNumerico)) {
      setError('CPF inválido. Verifique os dígitos informados.');
      return;
    }

    if (tipoPessoa === 'pj' && documentoNumerico.length !== 14) {
      setError('Informe um CNPJ válido com 14 dígitos.');
      return;
    }

    if (tipoPessoa === 'pj' && !validarCNPJ(documentoNumerico)) {
      setError('CNPJ inválido. Verifique os dígitos informados.');
      return;
    }

    try {
      setLoading(true);

      const dataToSend = Object.entries(formData).reduce((acc, [key, value]) => {
        acc[key] = value === '' ? null : value;
        return acc;
      }, {});

      // Garantir que o campo numero sempre seja incluído, mesmo se estiver vazio
      if (!('numero' in dataToSend)) {
        dataToSend.numero = formData.numero === '' ? null : formData.numero;
      }

      // Garantir que o campo complemento sempre seja incluído, mesmo se estiver vazio
      if (!('complemento' in dataToSend)) {
        dataToSend.complemento = formData.complemento === '' ? null : formData.complemento;
      }

      console.log('📤 Dados sendo enviados para atualização:', {
        ...dataToSend,
        numero: dataToSend.numero,
        numeroOriginal: formData.numero,
        complemento: dataToSend.complemento,
        complementoOriginal: formData.complemento
      });

      if (isEditMode) {
        await clientesAPI.atualizar(id, dataToSend);
        // Atualizar externalReference no formAsaas se já existir
        setFormAsaas((prev) => ({
          ...prev,
          externalReference: clienteUuid || prev.externalReference || ''
        }));
        // Avançar para o Step 2 (Cadastro Asaas)
        setError(null);
        setStep(2);
      } else {
        const response = await clientesAPI.criar(dataToSend);
        if (response.data?.success && response.data?.data?.id) {
          // Armazenar o ID do cliente criado para usar no Step 2
          setClienteId(String(response.data.data.id));
          setClienteUuid(response.data.data.uuid || null);
          // Atualizar externalReference no formAsaas
          setFormAsaas((prev) => ({
            ...prev,
            externalReference: response.data.data.uuid || String(response.data.data.id)
          }));
          // Avançar para o Step 2 (Cadastro Asaas)
          setError(null);
          setStep(2);
        } else {
          navigate('/clientes');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clientes');
  };

  // Atualizar dados do Asaas usando cadastro Haevo quando não existir registro na Asaas
  useEffect(() => {
    if (step === 3 && !asaasConsulta.found) {
      preencherFormularioComDadosCliente();
    }
  }, [step, asaasConsulta.found, preencherFormularioComDadosCliente]);

  useEffect(() => {
    if (step === 3) {
      consultarAsaas();
    } else {
      setAsaasConsulta(asaasConsultaInitialState);
    }
  }, [step, consultarAsaas]);

  // Carregar parâmetros de cobrança quando entrar no step 4
  useEffect(() => {
    const carregarParametrosCobranca = async () => {
      if (step === 4 && clienteId) {
        try {
          const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
          if (clienteIdNum) {
            const response = await parametrosCobrancaAPI.buscarPorCliente(clienteIdNum);
            if (response.data.success && response.data.data) {
              const dados = response.data.data;
              setFormParametrosCobranca(dados);
              // Salvar valores originais para comparação
              setParametrosCobrancaOriginal(JSON.parse(JSON.stringify(dados)));
              setTemParametrosCobrancaNoBanco(true);
            } else {
              // Não há registro no banco
              setParametrosCobrancaOriginal(null);
              setTemParametrosCobrancaNoBanco(false);
            }
          }
        } catch (err) {
          console.error('Erro ao carregar parâmetros de cobrança:', err);
          // Se der erro, assumir que não há registro
          setParametrosCobrancaOriginal(null);
          setTemParametrosCobrancaNoBanco(false);
        }
      }
    };
    carregarParametrosCobranca();
  }, [step, clienteId]);

  // Carregar cartões de crédito quando entrar no step 5
  useEffect(() => {
    const carregarCartoes = async () => {
      if (step === 5 && clienteId) {
        try {
          const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
          if (clienteIdNum) {
            const response = await cartoesCreditoAPI.buscarPorCliente(clienteIdNum);
            if (response.data.success) {
              setCartoes(response.data.data || []);
            }
          }
        } catch (err) {
          console.error('Erro ao carregar cartões:', err);
        }
      }
    };
    carregarCartoes();
  }, [step, clienteId]);

  // Carregar parâmetros do sistema quando entrar no step 2
  useEffect(() => {
    const carregarParametrosSistema = async () => {
      if (step === 2 && clienteId) {
        try {
          const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
          if (clienteIdNum) {
            // Buscar configuração existente pelo cliente
            const response = await configuracoesAPI.listar({ id_cliente: clienteIdNum });
            if (response.data.success && response.data.data && response.data.data.length > 0) {
              const config = response.data.data[0];
              setFormParametrosSistema({
                supabase_imagens_base_url: config.supabase_imagens_base_url || '',
                evolution_api_url: config.evolution_api_url || '',
                evolution_api_key: config.evolution_api_key || '',
                evolution_instance: config.evolution_instance || ''
              });
              setParametrosSistemaOriginal(JSON.parse(JSON.stringify({
                supabase_imagens_base_url: config.supabase_imagens_base_url || '',
                evolution_api_url: config.evolution_api_url || '',
                evolution_api_key: config.evolution_api_key || '',
                evolution_instance: config.evolution_instance || ''
              })));
              setTemParametrosSistemaNoBanco(true);
              setConfiguracaoUuid(config.uuid || null);
            } else {
              setParametrosSistemaOriginal(null);
              setTemParametrosSistemaNoBanco(false);
              setConfiguracaoUuid(null);
            }
          }
        } catch (err) {
          console.error('Erro ao carregar parâmetros do sistema:', err);
          setParametrosSistemaOriginal(null);
          setTemParametrosSistemaNoBanco(false);
          setConfiguracaoUuid(null);
        }
      }
    };
    carregarParametrosSistema();
  }, [step, clienteId]);

  const handleAsaasChange = (field, value) => {
    setFormAsaas((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNextStep = () => {
    setError(null);
    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    setError(null);
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSaveAsaas = async () => {
    setError(null);

    // Validações básicas
    if (!formAsaas.nome || !formAsaas.cpfCnpj) {
      setError('Nome e CPF/CNPJ são obrigatórios');
      return;
    }

    try {
      setLoading(true);

      const asaasData = montarPayloadAsaas();

      const clienteIdParaAsaas = clienteId || (isEditMode && id ? id : null);
      
      if (!clienteIdParaAsaas) {
        setError('ID do cliente não encontrado. Salve o cadastro Haevo primeiro.');
        return;
      }

      const response = await clientesAPI.criarAsaas(clienteIdParaAsaas, asaasData);

      if (response.data?.success) {
        setError(null);
        // Avançar para o próximo step (Parâmetros de Cobrança)
        setStep(4);
      } else {
        setError(response.data?.message || 'Erro ao salvar cliente na API Asaas');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error?.message || 
                          'Erro ao salvar cliente na API Asaas';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizarAsaas = async () => {
    setError(null);

    if (!formAsaas.nome || !formAsaas.cpfCnpj) {
      setError('Nome e CPF/CNPJ são obrigatórios');
      return;
    }

    const clienteIdParaAsaas = clienteUuid || clienteId;

    if (!clienteIdParaAsaas) {
      setError('ID do cliente não encontrado.');
      return;
    }

    if (!clienteAsaasId) {
      setError('Cliente não possui registro válido na Asaas para atualizar.');
      return;
    }

    try {
      setLoading(true);
      const asaasData = montarPayloadAsaas();
      await clientesAPI.atualizarAsaas(clienteIdParaAsaas, asaasData);
      await consultarAsaas();
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Erro ao atualizar cliente na API Asaas';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleParametrosCobrancaChange = (field, value) => {
    setFormParametrosCobranca((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Verificar se houve mudanças nos parâmetros de cobrança
  const temMudancasParametrosCobranca = () => {
    if (!temParametrosCobrancaNoBanco) {
      // Se não tem registro no banco, sempre permite salvar
      return true;
    }

    if (!parametrosCobrancaOriginal) {
      return true;
    }

    // Comparar todos os campos
    const campos = [
      'forma_pagamento_mensalidade',
      'dias_tolerancia_vencimento',
      'enviar_notificacao_antes_vencimento',
      'dias_antes_vencimento_notificacao',
      'enviar_notificacao_apos_vencimento',
      'aplicar_desconto_antecipacao',
      'percentual_desconto_antecipacao',
      'aplicar_multa_atraso',
      'percentual_multa',
      'aplicar_juros_atraso',
      'percentual_juros_mensal'
    ];

    for (const campo of campos) {
      const valorAtual = formParametrosCobranca[campo];
      const valorOriginal = parametrosCobrancaOriginal[campo];

      // Comparar valores, tratando números e booleanos
      if (typeof valorAtual === 'number' && typeof valorOriginal === 'number') {
        if (Math.abs(valorAtual - valorOriginal) > 0.001) {
          return true;
        }
      } else if (valorAtual !== valorOriginal) {
        return true;
      }
    }

    return false;
  };

  const handleSalvarParametrosCobranca = async () => {
    if (!clienteId) {
      setError('ID do cliente não encontrado');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
      await parametrosCobrancaAPI.salvar(clienteIdNum, formParametrosCobranca);
      setError(null);
      
      // Atualizar valores originais após salvar
      setParametrosCobrancaOriginal(JSON.parse(JSON.stringify(formParametrosCobranca)));
      setTemParametrosCobrancaNoBanco(true);
      
      // Avançar para o próximo step (Forma Pagamento)
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar parâmetros de cobrança');
    } finally {
      setLoading(false);
    }
  };

  const handleProximoParametrosCobranca = () => {
    // Apenas avança sem salvar (vai para Forma Pagamento)
    setStep(5);
  };

  // Funções para parâmetros do sistema
  const handleParametrosSistemaChange = (field, value) => {
    setFormParametrosSistema((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const removeTrailingSlash = (value) => {
    if (typeof value !== 'string') return value;
    let sanitized = value.trim();
    if (!sanitized) return sanitized;
    const protocolIndex = sanitized.indexOf('://');
    const minLength = protocolIndex >= 0 ? protocolIndex + 3 : 0;
    while (sanitized.length > Math.max(minLength, 0) && sanitized.endsWith('/')) {
      sanitized = sanitized.slice(0, -1);
    }
    return sanitized;
  };

  const temMudancasParametrosSistema = () => {
    if (!temParametrosSistemaNoBanco) {
      // Se não tem registro no banco, verifica se algum campo foi preenchido
      return formParametrosSistema.supabase_imagens_base_url ||
             formParametrosSistema.evolution_api_url ||
             formParametrosSistema.evolution_api_key ||
             formParametrosSistema.evolution_instance;
    }

    if (!parametrosSistemaOriginal) return true;

    const campos = ['supabase_imagens_base_url', 'evolution_api_url', 'evolution_api_key', 'evolution_instance'];
    for (const campo of campos) {
      if (formParametrosSistema[campo] !== parametrosSistemaOriginal[campo]) {
        return true;
      }
    }
    return false;
  };

  const handleSalvarParametrosSistema = async () => {
    if (!clienteId) {
      setError('ID do cliente não encontrado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
      const dadosParaEnvio = {
        id_cliente: clienteIdNum,
        supabase_imagens_base_url: removeTrailingSlash(formParametrosSistema.supabase_imagens_base_url),
        evolution_api_url: removeTrailingSlash(formParametrosSistema.evolution_api_url),
        evolution_api_key: formParametrosSistema.evolution_api_key,
        evolution_instance: formParametrosSistema.evolution_instance
      };

      if (temParametrosSistemaNoBanco && configuracaoUuid) {
        // Atualizar configuração existente
        await configuracoesAPI.atualizar(configuracaoUuid, dadosParaEnvio);
      } else {
        // Criar nova configuração
        const response = await configuracoesAPI.criar(dadosParaEnvio);
        if (response.data.success && response.data.data) {
          setConfiguracaoUuid(response.data.data.uuid || null);
        }
      }

      // Atualizar valores originais após salvar
      setParametrosSistemaOriginal(JSON.parse(JSON.stringify(formParametrosSistema)));
      setTemParametrosSistemaNoBanco(true);
      setError(null);
      
      // Avançar para o próximo step (Cadastro Asaas)
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar parâmetros do sistema');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarLinkCartao = async () => {
    if (!clienteUuid) {
      setError('UUID do cliente não encontrado');
      return;
    }

    if (!clienteId) {
      setError('ID do cliente não encontrado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Verificar se o cliente tem contrato ativo
      const contratoResponse = await contratosAPI.buscarContratoAtivoPorCliente(clienteId);
      
      if (!contratoResponse.data?.success || !contratoResponse.data?.data) {
        setError('O cliente não possui um contrato ativo. É necessário ter um contrato ativo para gerar o link de pagamento.');
        setLoading(false);
        return;
      }

      const response = await cartaoPublicoAPI.gerarToken(clienteUuid);
      
      if (response.data.success) {
        setLinkCartaoGerado(response.data.data.url);
        setLinkCopiado(false);
      } else {
        setError(response.data.message || 'Erro ao gerar link');
      }
    } catch (err) {
      // Se o erro for 404, significa que não há contrato ativo
      if (err.response?.status === 404) {
        setError('O cliente não possui um contrato ativo. É necessário ter um contrato ativo para gerar o link de pagamento.');
      } else {
        setError(err.response?.data?.message || 'Erro ao gerar link de cadastro de cartão');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopiarLinkCartao = async () => {
    if (linkCartaoGerado) {
      try {
        await navigator.clipboard.writeText(linkCartaoGerado);
        setLinkCopiado(true);
        setTimeout(() => setLinkCopiado(false), 2000);
      } catch (err) {
        setError('Erro ao copiar link');
      }
    }
  };

  const handleCartaoChange = (field, value) => {
    setFormCartao((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSalvarCartao = async () => {
    if (!clienteId) {
      setError('ID do cliente não encontrado');
      return;
    }

    if (!formCartao.holder_name || !formCartao.number_last_four || !formCartao.expiry_month || !formCartao.expiry_year || !formCartao.holder_cpf_cnpj) {
      setError('Preencha todos os campos obrigatórios do cartão');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
      const dadosCartao = {
        ...formCartao,
        cliente_id: clienteIdNum
      };

      if (editandoCartao) {
        await cartoesCreditoAPI.atualizar(editandoCartao.id, dadosCartao);
      } else {
        await cartoesCreditoAPI.criar(dadosCartao);
      }

      // Recarregar cartões
      const response = await cartoesCreditoAPI.buscarPorCliente(clienteIdNum);
      if (response.data.success) {
        setCartoes(response.data.data || []);
      }

      // Limpar formulário
      setFormCartao({
        holder_name: '',
        number_last_four: '',
        expiry_month: '',
        expiry_year: '',
        holder_cpf_cnpj: '',
        holder_email: '',
        holder_phone: '',
        holder_mobile_phone: '',
        holder_postal_code: '',
        holder_address: '',
        holder_address_number: '',
        holder_address_complement: '',
        padrao: false,
        observacoes: ''
      });
      setEditandoCartao(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar cartão de crédito');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarCartao = (cartao) => {
    setFormCartao({
      holder_name: cartao.holder_name || '',
      number_last_four: cartao.number_last_four || '',
      expiry_month: cartao.expiry_month || '',
      expiry_year: cartao.expiry_year || '',
      holder_cpf_cnpj: cartao.holder_cpf_cnpj || '',
      holder_email: cartao.holder_email || '',
      holder_phone: cartao.holder_phone || '',
      holder_mobile_phone: cartao.holder_mobile_phone || '',
      holder_postal_code: cartao.holder_postal_code || '',
      holder_address: cartao.holder_address || '',
      holder_address_number: cartao.holder_address_number || '',
      holder_address_complement: cartao.holder_address_complement || '',
      padrao: cartao.padrao || false,
      observacoes: cartao.observacoes || ''
    });
    setEditandoCartao(cartao);
  };

  const handleDeletarCartao = (cartao, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCartaoParaDeletar(cartao);
    setShowDeleteCartaoModal(true);
  };

  const handleConfirmarDeletarCartao = async () => {
    if (!cartaoParaDeletar) return;

    try {
      setDeletingCartao(true);
      setError(null);
      await cartoesCreditoAPI.deletar(cartaoParaDeletar.id);
      const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
      if (clienteIdNum) {
        const response = await cartoesCreditoAPI.buscarPorCliente(clienteIdNum);
        if (response.data.success) {
          setCartoes(response.data.data || []);
        }
      }
      setShowDeleteCartaoModal(false);
      setCartaoParaDeletar(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao deletar cartão de crédito');
    } finally {
      setDeletingCartao(false);
    }
  };

  const handleCancelarDeletarCartao = () => {
    setShowDeleteCartaoModal(false);
    setCartaoParaDeletar(null);
  };

  const handleAlternarStatusCartao = async (cartao) => {
    try {
      setLoading(true);
      setError(null);
      
      const novoStatus = !(cartao.ativo !== false);
      
      // Se estiver ativando o cartão, verificar se já existe um cartão ativo
      if (novoStatus) {
        const cartaoAtivoExistente = cartoes.find(c => 
          c.id !== cartao.id && 
          c.ativo !== false
        );
        
        if (cartaoAtivoExistente) {
          setError('Já existe um cartão ativo. Desative o cartão ativo antes de ativar outro.');
          setLoading(false);
          return;
        }
      }
      
      // Se estiver ativando o cartão, ele automaticamente se torna principal
      // Se estiver desativando, ele deixa de ser principal
      const dadosAtualizacao = {};
      
      if (novoStatus) {
        // Ao ativar: definir como principal e ativo
        // O backend processa padrao primeiro (remove dos outros) e depois atualiza este cartão
        dadosAtualizacao.padrao = true;
        dadosAtualizacao.ativo = true;
      } else {
        // Ao desativar: desativar e remover principal
        dadosAtualizacao.ativo = false;
        if (cartao.padrao) {
          dadosAtualizacao.padrao = false;
        }
      }
      
      await cartoesCreditoAPI.atualizar(cartao.id, dadosAtualizacao);
      
      // Recarregar cartões
      const clienteIdNum = typeof clienteId === 'string' ? parseInt(clienteId, 10) : clienteId;
      if (clienteIdNum) {
        const response = await cartoesCreditoAPI.buscarPorCliente(clienteIdNum);
        if (response.data.success) {
          setCartoes(response.data.data || []);
        }
      }
    } catch (err) {
      console.error('Erro ao alterar status do cartão:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao alterar status do cartão');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizar = () => {
    navigate('/clientes');
  };

  if (loadingData) {
    return (
      <Layout title={isEditMode ? 'Editando Cliente' : 'Novo Cliente'}>
        <Container>
          <LoadingContainer>
            <LoadingSpinner>
              <FiLoader />
            </LoadingSpinner>
            <LoadingText>Carregando dados...</LoadingText>
          </LoadingContainer>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditMode ? 'Editando Cliente' : 'Novo Cliente'}>
      <Container>
        <Header>
          <BackButton onClick={handleCancel}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>{isEditMode ? 'Editando Cliente' : 'Novo Cliente'}</Title>
        </Header>

        <Card>
          <Steps>
            <StepItem $active={step === 1}>
              <StepIndex $active={step === 1}>1</StepIndex>
              Cadastro
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 2}>
              <StepIndex $active={step === 2}>2</StepIndex>
              Parâmetros
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 3}>
              <StepIndex $active={step === 3}>3</StepIndex>
              Asaas
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 4}>
              <StepIndex $active={step === 4}>4</StepIndex>
              Cobrança
            </StepItem>
            <FiChevronRight />
            <StepItem $active={step === 5}>
              <StepIndex $active={step === 5}>5</StepIndex>
              Pagamento
            </StepItem>
          </Steps>

          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}

          {step === 1 && (
            <Form onSubmit={handleSubmit}>
          {/* Dados Básicos */}
          <FormSection>
            <SectionTitle>
              <FiUser />
              Dados Básicos
            </SectionTitle>

            <Grid>
              <FormGroup>
                <Label>
                  Nome Fantasia
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.nome_fantasia || ''}
                  onChange={(e) => handleInputChange('nome_fantasia', e.target.value)}
                  required
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Nome Social</Label>
                <Input
                  type="text"
                  value={formData.nome_social || ''}
                  onChange={(e) => handleInputChange('nome_social', e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Tipo de Pessoa</Label>
                <Select
                  value={formData.tipo_pessoa || ''}
                  onChange={(e) => handleInputChange('tipo_pessoa', e.target.value)}
                  disabled={loading}
                >
                  <option value="">Selecione...</option>
                  <option value="fisica">Física</option>
                  <option value="pj">Jurídica</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>CPF/CNPJ</Label>
                <Input
                  type="text"
                  value={formData.cpf_cnpj || ''}
                  onChange={(e) => handleInputMaskedChange('cpf_cnpj', e.target.value, 'cpf_cnpj')}
                  disabled={loading}
                  maxLength={18}
                  inputMode="numeric"
                />
              </FormGroup>
            </Grid>
          </FormSection>

          {/* Informações de Contato */}
          <FormSection>
            <SectionTitle>
              <FiPhone />
              Informações de Contato
            </SectionTitle>

            <Grid>
              <FormGroup>
                <Label>Nome do Contato</Label>
                <Input
                  type="text"
                  value={formData.nome_contato || ''}
                  onChange={(e) => handleInputChange('nome_contato', e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Telefone</Label>
                <Input
                  type="text"
                  value={formData.fone_contato || ''}
                  onChange={(e) => handleInputMaskedChange('fone_contato', e.target.value, 'telefone')}
                  disabled={loading}
                  maxLength={15}
                  inputMode="numeric"
                />
              </FormGroup>

              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.emial_contato || ''}
                  onChange={(e) => handleInputChange('emial_contato', e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Status
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.ativo === true ? 'true' : formData.ativo === false ? 'false' : 'true'}
                  onChange={(e) => handleInputChange('ativo', e.target.value === 'true')}
                  disabled={loading}
                  required
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </Select>
              </FormGroup>
            </Grid>
          </FormSection>

          {/* Endereço */}
          <FormSection>
            <SectionTitle>
              <FiHome />
              Endereço
            </SectionTitle>

            <Grid>
              <FormGroup>
                <Label>Endereço</Label>
                <Input
                  type="text"
                  value={formData.endereco || ''}
                  onChange={(e) => handleInputChange('endereco', e.target.value)}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>Número</Label>
                <Input
                  type="text"
                  value={formData.numero || ''}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  disabled={loading}
                  placeholder="Ex.: 150"
                />
              </FormGroup>

              <FormGroup>
                <Label>Complemento</Label>
                <Input
                  type="text"
                  value={formData.complemento || ''}
                  onChange={(e) => handleInputChange('complemento', e.target.value)}
                  disabled={loading}
                  placeholder="Ex.: Sala 201, Apto 302"
                />
              </FormGroup>

              <FormGroup>
                <Label>CEP</Label>
                <Input
                  type="text"
                  value={formData.cep || ''}
                  onChange={(e) => handleInputMaskedChange('cep', e.target.value, 'cep')}
                  disabled={loading}
                  maxLength={9}
                  inputMode="numeric"
                />
              </FormGroup>

              <FormGroup>
                <Label>Estado</Label>
                {isEditMode ? (
                  <Input
                    type="text"
                    value={formData.estado || ''}
                    disabled
                  />
                ) : (
                  <Select
                    value={formData.estado || ''}
                    onChange={(e) => handleEstadoChange(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Selecione...</option>
                    {estados.map((uf) => (
                      <option key={uf.id} value={uf.sigla}>{`${uf.nome} (${uf.sigla})`}</option>
                    ))}
                  </Select>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Cidade</Label>
                {isEditMode ? (
                  <Input
                    type="text"
                    value={formData.cidade || ''}
                    disabled
                  />
                ) : (
                  <Select
                    value={formData.cidade || ''}
                    onChange={(e) => handleInputChange('cidade', e.target.value)}
                    disabled={loading || !formData.estado}
                  >
                    <option value="">Selecione...</option>
                    {cidades.map((c) => (
                      <option key={c.id} value={c.nome}>{c.nome}</option>
                    ))}
                  </Select>
                )}
              </FormGroup>
            </Grid>
          </FormSection>

          {/* Observações */}
          <FormSection>
            <FormGroup>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes || ''}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                disabled={loading}
                placeholder="Observações adicionais..."
              />
            </FormGroup>
          </FormSection>

              <Actions>
                <CancelButton type="button" onClick={handleCancel} disabled={loading}>
                  Cancelar
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? <FiLoader /> : <FiSave />}
                  {loading ? 'Salvando...' : 'Salvar'}
                </SaveButton>
              </Actions>
            </Form>
          )}

          {step === 3 && (
            <Form>
              <FormSection>
                <SectionTitle>
                  <FiUser />
                  Dados do Cadastro Asaas
                </SectionTitle>

                {asaasConsulta.loading && (
                  <InfoMessage>Consultando registro existente na Asaas...</InfoMessage>
                )}
                {asaasConsulta.found && (
                  <InfoMessage>
                    Encontramos este cliente na Asaas. Os dados exibidos abaixo foram carregados
                    diretamente de lá. Use o botão Atualizar para editar na próxima etapa.
                  </InfoMessage>
                )}
                {asaasConsulta.error && (
                  <ErrorMessage>{asaasConsulta.error}</ErrorMessage>
                )}

                <Grid>
                  <FormGroup>
                    <Label>
                      Nome <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.nome || ''}
                      onChange={(e) => handleAsaasChange('nome', e.target.value)}
                      placeholder="Nome do cliente no Asaas"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      CPF/CNPJ <Required>*</Required>
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.cpfCnpj || ''}
                      onChange={(e) => handleAsaasChange('cpfCnpj', e.target.value.replace(/\D/g, ''))}
                      placeholder="Apenas números (CPF: 11 dígitos, CNPJ: 14 dígitos)"
                      disabled={loading}
                      maxLength={18}
                      inputMode="numeric"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={formAsaas.email || ''}
                      onChange={(e) => handleAsaasChange('email', e.target.value)}
                      placeholder="email@exemplo.com"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Telefone Celular
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.mobilePhone || ''}
                      onChange={(e) => handleAsaasChange('mobilePhone', e.target.value.replace(/\D/g, ''))}
                      placeholder="Apenas números"
                      disabled={loading}
                      maxLength={11}
                      inputMode="numeric"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Endereço
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.address || ''}
                      onChange={(e) => handleAsaasChange('address', e.target.value)}
                      placeholder="Ex.: Av. Paulista"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Número
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.addressNumber || ''}
                      onChange={(e) => handleAsaasChange('addressNumber', e.target.value)}
                      placeholder="Ex.: 150"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Complemento
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.complement || ''}
                      onChange={(e) => handleAsaasChange('complement', e.target.value)}
                      placeholder="Ex.: Sala 201"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Bairro
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.province || ''}
                      onChange={(e) => handleAsaasChange('province', e.target.value)}
                      placeholder="Ex.: Centro"
                      disabled={loading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      CEP
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.postalCode || ''}
                      onChange={(e) => {
                        const apenasNumeros = e.target.value.replace(/\D/g, '');
                        const cepFormatado = apenasNumeros.length <= 5
                          ? apenasNumeros
                          : `${apenasNumeros.slice(0, 5)}-${apenasNumeros.slice(5, 8)}`;
                        handleAsaasChange('postalCode', cepFormatado);
                      }}
                      placeholder="00000-000"
                      disabled={loading}
                      maxLength={9}
                      inputMode="numeric"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Referência Externa
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.externalReference || ''}
                      disabled
                      placeholder="ID do cliente (preenchido automaticamente)"
                    />
                  </FormGroup>
                </Grid>

                <Grid>
                  <FormGroup>
                    <Label>
                      Notificações Desabilitadas
                    </Label>
                    <Select
                      value={formAsaas.notificationDisabled === true ? 'true' : formAsaas.notificationDisabled === false ? 'false' : 'false'}
                      onChange={(e) => handleAsaasChange('notificationDisabled', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="false">Habilitadas</option>
                      <option value="true">Desabilitadas</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Cliente Estrangeiro
                    </Label>
                    <Select
                      value={formAsaas.foreignCustomer === true ? 'true' : formAsaas.foreignCustomer === false ? 'false' : 'false'}
                      onChange={(e) => handleAsaasChange('foreignCustomer', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="false">Não</option>
                      <option value="true">Sim</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Nome do Grupo
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.groupName || 'HaevoRevendas'}
                      disabled
                      placeholder="HaevoRevendas"
                    />
                    <HelperText>Valor fixo: HaevoRevendas</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Emails Adicionais
                    </Label>
                    <Input
                      type="text"
                      value={formAsaas.additionalEmails || ''}
                      onChange={(e) => handleAsaasChange('additionalEmails', e.target.value)}
                      placeholder="email1@exemplo.com,email2@exemplo.com"
                      disabled={loading}
                    />
                    <HelperText>Separe múltiplos emails por vírgula</HelperText>
                  </FormGroup>
                </Grid>
              </FormSection>

              <NavigationBar>
                <StepsButtonGroup>
                  <GhostButton type="button" onClick={handlePreviousStep}>
                    Voltar
                  </GhostButton>
                </StepsButtonGroup>

                <StepsButtonGroup>
                  {asaasConsulta.found && (
                    <>
                      <UpdateButton
                        type="button"
                        onClick={handleAtualizarAsaas}
                        disabled={loading || asaasConsulta.loading}
                      >
                        <FiRefreshCw />
                        Atualizar
                      </UpdateButton>
                      <GhostButton
                        type="button"
                        onClick={() => setStep(4)}
                        disabled={loading || asaasConsulta.loading}
                      >
                        Próximo
                        <FiChevronRight />
                      </GhostButton>
                    </>
                  )}
                  {!asaasConsulta.found && (
                    <>
                      <PrimaryButton
                        type="button"
                        onClick={handleSaveAsaas}
                        disabled={loading || asaasConsulta.loading}
                      >
                        <FiSave />
                        {loading ? 'Salvando...' : 'Salvar e Continuar'}
                      </PrimaryButton>
                      <GhostButton
                        type="button"
                        onClick={() => setStep(4)}
                        disabled={loading || asaasConsulta.loading}
                      >
                        Próximo
                        <FiChevronRight />
                      </GhostButton>
                    </>
                  )}
                </StepsButtonGroup>
              </NavigationBar>
            </Form>
          )}

          {step === 4 && (
            <Form>
              <FormSection>
                <SectionTitle>
                  <FiDollarSign />
                  Parâmetros de Cobrança
                </SectionTitle>

                <Grid>
                  <FormGroup>
                    <Label>
                      Forma de Pagamento da Mensalidade
                      <Required>*</Required>
                    </Label>
                    <Select
                      value={formParametrosCobranca.forma_pagamento_mensalidade}
                      onChange={(e) => handleParametrosCobrancaChange('forma_pagamento_mensalidade', e.target.value)}
                      disabled={loading}
                    >
                      <option value="C">Cartão de Crédito</option>
                      <option value="P">Pix Automático</option>
                      <option value="B">Boleto</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Dias de Tolerância Após Vencimento
                      <Required>*</Required>
                    </Label>
                    <Input
                      type="number"
                      value={formParametrosCobranca.dias_tolerancia_vencimento}
                      onChange={(e) => handleParametrosCobrancaChange('dias_tolerancia_vencimento', parseInt(e.target.value) || 0)}
                      disabled={loading}
                      min="0"
                    />
                    <HelperText>Dias após o vencimento em que o sistema deixa de funcionar</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Enviar Notificação Antes do Vencimento
                    </Label>
                    <Select
                      value={formParametrosCobranca.enviar_notificacao_antes_vencimento ? 'true' : 'false'}
                      onChange={(e) => handleParametrosCobrancaChange('enviar_notificacao_antes_vencimento', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Select>
                  </FormGroup>

                  {formParametrosCobranca.enviar_notificacao_antes_vencimento && (
                    <FormGroup>
                      <Label>
                        Dias Antes do Vencimento para Notificação
                      </Label>
                      <Input
                        type="number"
                        value={formParametrosCobranca.dias_antes_vencimento_notificacao}
                        onChange={(e) => handleParametrosCobrancaChange('dias_antes_vencimento_notificacao', parseInt(e.target.value) || 0)}
                        disabled={loading}
                        min="0"
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label>
                      Enviar Notificação Após Vencimento
                    </Label>
                    <Select
                      value={formParametrosCobranca.enviar_notificacao_apos_vencimento ? 'true' : 'false'}
                      onChange={(e) => handleParametrosCobrancaChange('enviar_notificacao_apos_vencimento', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Aplicar Desconto por Antecipação
                    </Label>
                    <Select
                      value={formParametrosCobranca.aplicar_desconto_antecipacao ? 'true' : 'false'}
                      onChange={(e) => handleParametrosCobrancaChange('aplicar_desconto_antecipacao', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="false">Não</option>
                      <option value="true">Sim</option>
                    </Select>
                  </FormGroup>

                  {formParametrosCobranca.aplicar_desconto_antecipacao && (
                    <FormGroup>
                      <Label>
                        Percentual de Desconto (%)
                      </Label>
                      <Input
                        type="number"
                      step="0.01"
                        value={formParametrosCobranca.percentual_desconto_antecipacao}
                        onChange={(e) => handleParametrosCobrancaChange('percentual_desconto_antecipacao', parseFloat(e.target.value) || 0)}
                        disabled={loading}
                        min="0"
                        max="100"
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label>
                      Aplicar Multa por Atraso
                    </Label>
                    <Select
                      value={formParametrosCobranca.aplicar_multa_atraso ? 'true' : 'false'}
                      onChange={(e) => handleParametrosCobrancaChange('aplicar_multa_atraso', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Select>
                  </FormGroup>

                  {formParametrosCobranca.aplicar_multa_atraso && (
                    <FormGroup>
                      <Label>
                        Percentual de Multa (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formParametrosCobranca.percentual_multa}
                        onChange={(e) => handleParametrosCobrancaChange('percentual_multa', parseFloat(e.target.value) || 0)}
                        disabled={loading}
                        min="0"
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label>
                      Aplicar Juros por Atraso
                    </Label>
                    <Select
                      value={formParametrosCobranca.aplicar_juros_atraso ? 'true' : 'false'}
                      onChange={(e) => handleParametrosCobrancaChange('aplicar_juros_atraso', e.target.value === 'true')}
                      disabled={loading}
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Select>
                  </FormGroup>

                  {formParametrosCobranca.aplicar_juros_atraso && (
                    <FormGroup>
                      <Label>
                        Percentual de Juros Mensal (%)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formParametrosCobranca.percentual_juros_mensal}
                        onChange={(e) => handleParametrosCobrancaChange('percentual_juros_mensal', parseFloat(e.target.value) || 0)}
                        disabled={loading}
                        min="0"
                      />
                    </FormGroup>
                  )}
                </Grid>
              </FormSection>

              <NavigationBar>
                <StepsButtonGroup>
                  <GhostButton type="button" onClick={handlePreviousStep}>
                    Voltar
                  </GhostButton>
                </StepsButtonGroup>

                <StepsButtonGroup>
                  <PrimaryButton
                    type="button"
                    onClick={handleSalvarParametrosCobranca}
                    disabled={loading || !temMudancasParametrosCobranca()}
                  >
                    <FiSave />
                    {loading ? 'Salvando...' : 'Salvar e Continuar'}
                  </PrimaryButton>
                  <GhostButton
                    type="button"
                    onClick={handleProximoParametrosCobranca}
                    disabled={loading}
                  >
                    Próximo
                    <FiChevronRight />
                  </GhostButton>
                </StepsButtonGroup>
              </NavigationBar>
            </Form>
          )}

          {step === 5 && (
            <Form onSubmit={(e) => e.preventDefault()}>
              <FormSection>
                <SectionTitle>
                  <FiCreditCard />
                  Forma de Pagamento - Cartões de Crédito
                </SectionTitle>

                {/* Botão para gerar link de cadastro público */}
                <div style={{ 
                  marginBottom: theme.spacing[6], 
                  padding: theme.spacing[4], 
                  background: theme.colors.background.secondary, 
                  borderRadius: theme.borderRadius.lg,
                  border: `1px solid ${theme.colors.border.primary}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing[2] }}>
                    <div>
                      <Label style={{ marginBottom: theme.spacing[1] }}>Cadastro Público de Cartão</Label>
                      <p style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary, margin: 0 }}>
                        Gere um link seguro para o cliente cadastrar o cartão de crédito sem precisar estar logado no sistema. <strong>No momento do cadastro já será cobrada a primeira mensalidade.</strong>
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleGerarLinkCartao}
                      disabled={loading || !clienteUuid}
                      style={{ 
                        background: theme.colors.primary[500],
                        color: theme.colors.white,
                        border: 'none',
                        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                        minWidth: 'auto'
                      }}
                    >
                      <FiLink />
                      {linkCartaoGerado ? 'Link Gerado' : 'Gerar Link'}
                    </Button>
                  </div>
                  {linkCartaoGerado && (
                    <div style={{ 
                      marginTop: theme.spacing[4],
                      padding: theme.spacing[3],
                      background: theme.colors.background.primary,
                      borderRadius: theme.borderRadius.md,
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing[2]
                    }}>
                      <Input
                        type="text"
                        value={linkCartaoGerado}
                        readOnly
                        style={{ flex: 1, fontFamily: 'monospace', fontSize: theme.fontSizes.sm }}
                      />
                      <Button
                        type="button"
                        onClick={handleCopiarLinkCartao}
                        style={{ 
                          background: 'transparent',
                          color: theme.colors.primary[500],
                          border: `1px solid ${theme.colors.primary[500]}`,
                          padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
                          minWidth: 'auto'
                        }}
                      >
                        {linkCopiado ? <FiCheckCircle /> : <FiCopy />}
                      </Button>
                    </div>
                  )}
                </div>

              {cartoes.length > 0 && (
                <FormSection>
                  <SectionTitle>
                    <FiCreditCard />
                    Cartões Cadastrados
                  </SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                    {cartoes.map((cartao) => (
                      <div
                        key={cartao.id}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: theme.spacing[4],
                          border: `1px solid ${cartao.padrao ? theme.colors.primary[500] : theme.colors.border.primary}`,
                          borderRadius: theme.borderRadius.lg,
                          background: theme.colors.background.secondary
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing[2] }}>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing[1] }}>
                            <strong>{cartao.holder_name}</strong>
                            <CartaoTag $type={cartao.ativo !== false ? 'ativo' : 'inativo'}>
                              {cartao.ativo !== false ? 'Ativo' : 'Inativo'}
                            </CartaoTag>
                            {cartao.ativo !== false && cartao.padrao && (
                              <CartaoTag $type="principal">Principal</CartaoTag>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                            <ActionButton
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAlternarStatusCartao(cartao);
                              }}
                              title={cartao.ativo !== false ? "Desativar cartão (deixará de ser principal)" : "Ativar cartão (tornará principal)"}
                              style={{
                                color: cartao.ativo !== false ? theme.colors.success[500] : theme.colors.text.muted,
                                borderColor: cartao.ativo !== false ? theme.colors.success[500] : theme.colors.border.primary
                              }}
                            >
                              {cartao.ativo !== false ? <FiToggleRight /> : <FiToggleLeft />}
                            </ActionButton>
                            {/* Botão de excluir removido - não é permitido excluir cartões cadastrados */}
                          </div>
                        </div>
                        <div style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.muted }}>
                          <div>Cartão: **** **** **** {cartao.number_last_four}</div>
                          <div>Validade: {String(cartao.expiry_month).padStart(2, '0')}/{cartao.expiry_year}</div>
                          <div>CPF/CNPJ: {cartao.holder_cpf_cnpj}</div>
                          {cartao.holder_email && <div>Email: {cartao.holder_email}</div>}
                          {cartao.holder_phone && <div>Telefone Fixo: {cartao.holder_phone}</div>}
                          {cartao.holder_mobile_phone && <div>Telefone Celular: {cartao.holder_mobile_phone}</div>}
                          {(cartao.holder_address || cartao.holder_address_number || cartao.holder_postal_code) && (
                            <div>
                              Endereço: {[
                                cartao.holder_address,
                                cartao.holder_address_number && `nº ${cartao.holder_address_number}`,
                                cartao.holder_address_complement
                              ].filter(Boolean).join(', ')}
                              {cartao.holder_postal_code && ` - CEP: ${cartao.holder_postal_code}`}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </FormSection>
              )}
              </FormSection>

              <NavigationBar>
                <StepsButtonGroup>
                  <GhostButton type="button" onClick={handlePreviousStep}>
                    Voltar
                  </GhostButton>
                </StepsButtonGroup>

                <StepsButtonGroup>
                  <PrimaryButton
                    type="button"
                    onClick={handleFinalizar}
                    disabled={loading}
                  >
                    <FiSave />
                    Finalizar
                  </PrimaryButton>
                </StepsButtonGroup>
              </NavigationBar>
            </Form>
          )}

          {step === 2 && (
            <Form onSubmit={(e) => e.preventDefault()}>
              <FormSection>
                <SectionTitle>
                  <FiSettings />
                  Parâmetros do Sistema
                </SectionTitle>

                <Grid>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>URL Base de Imagens (Supabase)</Label>
                    <Input
                      type="url"
                      value={formParametrosSistema.supabase_imagens_base_url}
                      onChange={(e) => handleParametrosSistemaChange('supabase_imagens_base_url', e.target.value)}
                      placeholder="https://exemplo.supabase.co/storage/v1/object/public/..."
                      disabled={loading}
                    />
                    <HelperText>URL base do Supabase Storage para imagens</HelperText>
                  </FormGroup>
                </Grid>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <FiSettings />
                  WhatsApp
                </SectionTitle>

                <Grid>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Host</Label>
                    <Input
                      type="url"
                      value={formParametrosSistema.evolution_api_url}
                      onChange={(e) => handleParametrosSistemaChange('evolution_api_url', e.target.value)}
                      placeholder="https://api.evolutionapi.com"
                      disabled={loading}
                    />
                    <HelperText>URL base da Evolution API para WhatsApp</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Token</Label>
                    <Input
                      type="text"
                      value={formParametrosSistema.evolution_api_key}
                      onChange={(e) => handleParametrosSistemaChange('evolution_api_key', e.target.value)}
                      placeholder="sua-chave-api-aqui"
                      disabled={loading}
                    />
                    <HelperText>Chave de autenticação da Evolution API</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Subdomain</Label>
                    <Input
                      type="text"
                      value={formParametrosSistema.evolution_instance}
                      onChange={(e) => handleParametrosSistemaChange('evolution_instance', e.target.value)}
                      placeholder="nome-da-instancia"
                      disabled={loading}
                    />
                    <HelperText>Nome da instância do WhatsApp Business</HelperText>
                  </FormGroup>
                </Grid>
              </FormSection>

              <NavigationBar>
                <StepsButtonGroup>
                  <GhostButton type="button" onClick={handlePreviousStep}>
                    Voltar
                  </GhostButton>
                </StepsButtonGroup>

                <StepsButtonGroup>
                  <PrimaryButton
                    type="button"
                    onClick={handleSalvarParametrosSistema}
                    disabled={loading || !temMudancasParametrosSistema()}
                  >
                    <FiSave />
                    {loading ? 'Salvando...' : 'Salvar e Continuar'}
                  </PrimaryButton>
                  <GhostButton
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={loading}
                  >
                    Próximo
                    <FiChevronRight />
                  </GhostButton>
                </StepsButtonGroup>
              </NavigationBar>
            </Form>
          )}

          {step === 1 && (
            <NavigationBar>
              <StepsButtonGroup>
                <div></div>
              </StepsButtonGroup>
              <StepsButtonGroup>
                <GhostButton
                  type="button"
                  onClick={handleNextStep}
                  disabled={loading}
                >
                  Próximo
                  <FiChevronRight />
                </GhostButton>
              </StepsButtonGroup>
            </NavigationBar>
          )}
        </Card>
      </Container>

      {/* Modal de confirmação de exclusão de cartão */}
      {showDeleteCartaoModal && (
        <ModalOverlay onClick={handleCancelarDeletarCartao}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Excluir Cartão de Crédito</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este cartão de crédito? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O cartão de crédito de <strong>{cartaoParaDeletar?.holder_name}</strong>
              <br />
              • Cartão finalizado em {cartaoParaDeletar?.number_last_four}
              <br />
              • Todos os dados relacionados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarDeletarCartao} disabled={deletingCartao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleConfirmarDeletarCartao} disabled={deletingCartao}>
                <FiTrash2 />
                {deletingCartao ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default ClienteForm;

